import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { PageSeo } from "@/components/PageSeo";
import { useLocalizedPath } from "@/hooks/useLocalizedPath";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { 
  Sparkles, 
  Upload, 
  Palette, 
  Download, 
  Clock, 
  CheckCircle2, 
  Building2, 
  User, 
  Home,
  ArrowRight,
  RefreshCw
} from "lucide-react";

const About = () => {
  const { t } = useTranslation();
  const { buildPath } = useLocalizedPath();

  const benefits = [
    { key: "easy", icon: CheckCircle2 },
    { key: "fast", icon: Clock },
    { key: "realistic", icon: Sparkles },
    { key: "costEffective", icon: Building2 },
    { key: "perfect", icon: Home },
  ];

  const targetGroups = [
    { key: "realtors", icon: Building2 },
    { key: "private", icon: User },
    { key: "developers", icon: Home },
    { key: "anyone", icon: CheckCircle2 },
  ];

  const steps = [
    { key: "upload", icon: Upload },
    { key: "ai", icon: Sparkles },
    { key: "download", icon: Download },
  ];

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <PageSeo />
      
      <Header />

      <main className="flex-1">
        {/* Hero Section */}
        <section className="container mx-auto px-4 py-12 sm:py-16 lg:py-20">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-foreground mb-6">
              {t('about.hero.title')}
            </h1>
            <p className="text-lg sm:text-xl text-muted-foreground mb-8 max-w-3xl mx-auto">
              {t('about.hero.description')}
            </p>
          </div>
        </section>

        {/* What is E-styling */}
        <section className="bg-muted/20 py-12 sm:py-16 lg:py-20">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-2xl sm:text-3xl font-bold text-foreground mb-6">
                {t('about.whatIs.title')}
              </h2>
              <p className="text-muted-foreground text-lg leading-relaxed">
                {t('about.whatIs.description')}
              </p>
            </div>
          </div>
        </section>

        {/* How it works */}
        <section className="container mx-auto px-4 py-12 sm:py-16 lg:py-20">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl sm:text-3xl font-bold text-foreground mb-4 text-center">
              {t('about.howItWorks.title')}
            </h2>
            <p className="text-muted-foreground text-center mb-10 max-w-2xl mx-auto">
              {t('about.howItWorks.description')}
            </p>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {steps.map((step, index) => {
                const Icon = step.icon;
                return (
                  <Card key={step.key} className="p-6 text-center relative">
                    <div className="absolute -top-3 -left-3 bg-primary text-primary-foreground w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm">
                      {index + 1}
                    </div>
                    <div className="bg-primary/10 w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-4">
                      <Icon className="h-6 w-6 text-primary" />
                    </div>
                    <h3 className="font-semibold text-foreground mb-2">
                      {t(`about.howItWorks.steps.${step.key}.title`)}
                    </h3>
                    <p className="text-sm text-muted-foreground">
                      {t(`about.howItWorks.steps.${step.key}.description`)}
                    </p>
                  </Card>
                );
              })}
            </div>
          </div>
        </section>

        {/* Why use AI */}
        <section className="bg-muted/20 py-12 sm:py-16 lg:py-20">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-2xl sm:text-3xl font-bold text-foreground mb-4 text-center">
                {t('about.whyAI.title')}
              </h2>
              <p className="text-muted-foreground text-center mb-10 max-w-2xl mx-auto">
                {t('about.whyAI.description')}
              </p>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {benefits.map((benefit) => {
                  const Icon = benefit.icon;
                  return (
                    <div key={benefit.key} className="flex items-start gap-3 p-4 rounded-lg bg-background border border-border">
                      <Icon className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
                      <span className="text-foreground">
                        {t(`about.whyAI.benefits.${benefit.key}`)}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </section>

        {/* Target groups */}
        <section className="container mx-auto px-4 py-12 sm:py-16 lg:py-20">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl sm:text-3xl font-bold text-foreground mb-8 text-center">
              {t('about.forWhom.title')}
            </h2>
            <div className="grid sm:grid-cols-2 gap-4">
              {targetGroups.map((group) => {
                const Icon = group.icon;
                return (
                  <Card key={group.key} className="p-5 flex items-start gap-4">
                    <div className="bg-primary/10 w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0">
                      <Icon className="h-5 w-5 text-primary" />
                    </div>
                    <p className="text-foreground">
                      {t(`about.forWhom.groups.${group.key}`)}
                    </p>
                  </Card>
                );
              })}
            </div>
          </div>
        </section>

        {/* Replace Furniture Feature */}
        <section className="container mx-auto px-4 py-12 sm:py-16 lg:py-20">
          <div className="max-w-4xl mx-auto">
            <div className="flex items-center gap-4 mb-6">
              <div className="bg-primary/10 w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0">
                <RefreshCw className="h-6 w-6 text-primary" />
              </div>
              <h2 className="text-2xl sm:text-3xl font-bold text-foreground">
                {t('about.replaceFurniture.title')}
              </h2>
            </div>
            <p className="text-muted-foreground text-lg leading-relaxed">
              {t('about.replaceFurniture.description')}
            </p>
          </div>
        </section>

        {/* AI vs Traditional */}
        <section className="bg-muted/20 py-12 sm:py-16 lg:py-20">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-2xl sm:text-3xl font-bold text-foreground mb-6 text-center">
                {t('about.comparison.title')}
              </h2>
              <div className="grid md:grid-cols-2 gap-6">
                <Card className="p-6 border-muted">
                  <h3 className="font-semibold text-foreground mb-3">
                    {t('about.comparison.traditional.title')}
                  </h3>
                  <p className="text-muted-foreground text-sm">
                    {t('about.comparison.traditional.description')}
                  </p>
                </Card>
                <Card className="p-6 border-primary/50 bg-primary/5">
                  <h3 className="font-semibold text-foreground mb-3">
                    {t('about.comparison.ai.title')}
                  </h3>
                  <p className="text-muted-foreground text-sm">
                    {t('about.comparison.ai.description')}
                  </p>
                </Card>
              </div>
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="bg-primary text-primary-foreground py-12 sm:py-16 lg:py-20">
          <div className="container mx-auto px-4 text-center">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold mb-4">
              {t('about.cta.title')}
            </h2>
            <p className="text-lg mb-8 opacity-90 max-w-2xl mx-auto">
              {t('about.cta.description')}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to={buildPath('/auth')}>
                <Button size="lg" variant="secondary" className="gap-2 w-full sm:w-auto">
                  <Sparkles className="h-5 w-5" />
                  {t('about.cta.tryNow')}
                </Button>
              </Link>
              <Link to={buildPath('/examples')}>
                <Button size="lg" variant="outline" className="gap-2 w-full sm:w-auto bg-transparent border-primary-foreground/30 text-primary-foreground hover:bg-primary-foreground/10">
                  {t('about.cta.seeExamples')}
                  <ArrowRight className="h-4 w-4" />
                </Button>
              </Link>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default About;
